import { GALLERY_CONFIG } from '../gallery-config.js';
import { cartService } from '../services/cart/cart-service.js';

/**
 * Configuration constants for gallery builder actions
 * @private
 */
const ACTION_CONSTANTS = {
  MIN_SEARCH_QUERY_LENGTH: 2,
  NAVIGATION_FALLBACK_TIMEOUT_MS: 150,
  THIRD_ARTWORK_POSITION: 2,
  LAYOUT_COUNT_TRIO: 3,
  LAYOUT_COUNT_DUO: 2,
  HOME_URL: '/'
};

/**
 * @typedef {Object} ArtworkObject
 * @property {string|number} id - Unique artwork identifier
 * @property {string} title - Artwork title
 * @property {string} handle - URL handle
 * @property {Object} image - Image data
 */

/**
 * @typedef {Object} VariantConfig
 * @property {string|null} size - Selected size option
 * @property {string|null} frame - Selected frame option
 * @property {string|null} mount - Selected mount option
 * @property {boolean} locked - Whether variant is locked
 */

/**
 * Gallery Builder Actions - Public API for user interactions
 * Coordinates between Alpine.js stores (galleryCore, galleryUI, galleryProducts, galleryPreview)
 */
export const GalleryBuilderActions = {
  // ============================================================================
  // ARTWORK SELECTION & INTERACTION
  // ============================================================================

  /**
   * Sets the active artwork for carousel display
   * @param {number} index - Zero-based index of the artwork to activate
   * @returns {Promise<void>}
   */
  async setActiveArtwork(index) {
    await this.$store.galleryUI.setActiveArtwork(index);
  },

  closeCarousel() {
    this.$store.galleryUI.setActiveArtwork(null);
  },

  /**
   * @param {number} index - Zero-based index of the artwork to hover
   */
  setHoverArtwork(index) {
    this.$store.galleryUI.setHoverArtwork(index);
  },

  clearHover() {
    this.$store.galleryUI.clearHover();
  },

  /**
   * Selects an artwork for the gallery and activates it for viewing
   * @param {ArtworkObject} artwork - The artwork object to select
   * @param {number} position - Position in the gallery layout (0-indexed)
   * @returns {void}
   */
  selectArtwork(artwork, position) {
    this.$store.galleryCore.selectArtwork(artwork, position);
    this.setActiveArtwork(position);
    this.initializeSwymWishlist();
  },

  // ============================================================================
  // LAYOUT & ORIENTATION MANAGEMENT
  // ============================================================================

  /**
   * Switches the gallery layout to a different artwork count
   * Regenerates randomize queue and validates mobile artwork index
   * @param {number} newCount - Number of artworks in the new layout (2 or 3)
   * @returns {Promise<void>}
   */
  async switchLayout(newCount) {
    await this.$store.galleryCore.switchLayout(newCount);
    this.validateMobileArtworkIndex();
    this.initializeSwymWishlist();

    const queueStore = this.$store.galleryRandomizeQueue;
    if (queueStore) {
      queueStore.regenerateQueue();
    }
  },

  /**
   * Switches gallery orientation between horizontal and vertical
   * @param {string} newOrientation - Target orientation ('horizontal' or 'vertical')
   * @returns {Promise<void>}
   */
  async switchOrientation(newOrientation) {
    window._orientationChangeStartTime = performance.now();

    if (newOrientation === this.orientation) {
      return;
    }

    const currentActiveIndex = this.$store.galleryUI.active_artwork_index;
    if (currentActiveIndex !== null) {
      this.$store.galleryUI.last_active_position_before_orientation_change = currentActiveIndex;
    }

    this.closeCarousel();

    const savedVariantConfig = {
      size: this.$store.galleryCore.variant_selection.size,
      frame: this.$store.galleryCore.variant_selection.frame,
      mount: this.$store.galleryCore.variant_selection.mount,
      locked: this.$store.galleryCore.variant_selection.locked
    };

    const waitForNextFrame = async () => {
      if (typeof requestAnimationFrame !== 'function') return;
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    };

    this.$store.galleryCore.setAutoSaveEnabled(false);
    this.$store.galleryCore.gallery_visible = false;

    await waitForNextFrame();

    this.$store.galleryCore.resetVariantState(true);
    this.$store.galleryCore.switchToOrientation(newOrientation, savedVariantConfig);

    const coreStore = this.$store.galleryCore;
    const requiredCount = coreStore.layout_count;
    let orientationState = coreStore.orientation_states[newOrientation];
    let orientationValidCount = orientationState?.selected_artworks?.filter(artwork => artwork && artwork.id).length ?? 0;

    if (orientationValidCount >= requiredCount) {
      coreStore.selected_artworks = orientationState.selected_artworks
        .filter(artwork => artwork && artwork.id)
        .slice(0, requiredCount);
    } else {
      this.selectRandomDefaultsForOrientation?.(newOrientation);

      const refreshedState = coreStore.orientation_states[newOrientation];
      if (refreshedState?.selected_artworks?.length) {
        coreStore.selectArtworks(
          refreshedState.selected_artworks
            .filter(artwork => artwork && artwork.id)
            .slice(0, requiredCount)
        );
      }
      orientationState = refreshedState;
      orientationValidCount = orientationState?.selected_artworks?.filter(artwork => artwork && artwork.id).length ?? 0;
    }

    let validArtworkCount = coreStore.selected_artworks.filter(artwork => artwork && artwork.id).length;

    if (validArtworkCount === 0) {
      coreStore.selected_artworks = [];
      this.initializeDefaultSelection();
    } else if (coreStore.selected_artworks.length > requiredCount) {
      coreStore.selected_artworks = coreStore.selected_artworks.slice(0, requiredCount);
    }

    // If we're in trio mode but third_artwork_visible is false, try to restore it
    if (this.$store.galleryCore.layout_count === ACTION_CONSTANTS.LAYOUT_COUNT_TRIO && !this.$store.galleryCore.third_artwork_visible) {
      const latestValidCount = this.$store.galleryCore.selected_artworks.filter(artwork => artwork && artwork.id).length;
      if (latestValidCount >= ACTION_CONSTANTS.LAYOUT_COUNT_TRIO) {
        this.$store.galleryCore.third_artwork_visible = true;
      } else if (latestValidCount === ACTION_CONSTANTS.LAYOUT_COUNT_DUO) {
        // Try to add a third artwork from available pool
        await this.$store.galleryLayout.addThirdArtworkFromAvailable(
          this.$store.galleryCore,
          this.$store.galleryVariants
        );
        if (this.$store.galleryCore.selected_artworks.length === ACTION_CONSTANTS.LAYOUT_COUNT_TRIO) {
          this.$store.galleryCore.third_artwork_visible = true;
        }
      }
    }

    const activeSearchQuery = this.$store.galleryUI.active_search_query?.trim() || '';
    const hasActiveSearch = activeSearchQuery.length >= ACTION_CONSTANTS.MIN_SEARCH_QUERY_LENGTH;
    const searchService = this.searchService || window.gallerySearchService;

    if (window.galleryBoostBridge?.syncOrientationFilter) {
      window.galleryBoostBridge.syncOrientationFilter();
    }

    if (hasActiveSearch && searchService) {
      searchService.searchQuery = activeSearchQuery;
      searchService.setSearchingState(true);
      searchService.executeSearch(activeSearchQuery);
    } else if (window.galleryBoostBridge?.applyFilters) {
      await window.galleryBoostBridge.applyFilters({ skipLoadingScreen: true });
    }

    await waitForNextFrame();

    this.$store.galleryCore.gallery_visible = true;
    this.$store.galleryCore.setAutoSaveEnabled(true);

    if (!this.swymInitializedOnce) {
      this.initializeSwymWishlist();
      this.swymInitializedOnce = true;
    }

    // If search is active but service was unavailable, fall back to event dispatch
    if (hasActiveSearch && !searchService && typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('gallery:orientation-changed', {
        detail: {
          orientation: newOrientation,
          query: activeSearchQuery
        }
      }));
    }

    const savedPosition = this.$store.galleryUI.last_active_position_before_orientation_change;
    if (savedPosition !== null) {
      const newOrientationArtworks = this.$store.galleryCore.selected_artworks;
      const newLayoutCount = this.$store.galleryCore.layout_count;

      let positionToActivate = savedPosition;

      // Handle trio → duo transition: if 3rd was active (index 2), activate 2nd (index 1)
      if (savedPosition >= newLayoutCount) {
        positionToActivate = newLayoutCount - 1;
      }

      if (positionToActivate < newOrientationArtworks.length &&
          newOrientationArtworks[positionToActivate] &&
          newOrientationArtworks[positionToActivate].id) {
        this.setActiveArtwork(positionToActivate);
      }

      this.$store.galleryUI.last_active_position_before_orientation_change = null;
    }

    const queueStore = this.$store.galleryRandomizeQueue;
    if (queueStore) {
      queueStore.regenerateQueue();
    }

    // Clear the orientation change flag after carousel update completes
    setTimeout(() => {
      window._orientationChangeStartTime = null;
    }, 1000);
  },

  /**
   * Randomizes the current artwork selection using pre-randomized queue
   * @returns {Promise<void>}
   */
  async randomizeSelection() {
    await this.$store.galleryProducts.randomizeSelection();
    this.initializeSwymWishlist();
  },

  /**
   * Add selected artworks to cart
   * @param {Object} [detail] - Optional detail object with payload
   * @param {Array} [detail.payload] - Pre-prepared cart payload
   * @returns {Promise<void>}
   */
  async handleAddToCart(detail = null) {
    const payload = detail?.payload || this.$store.galleryCore.prepareCartPayload();

    if (!payload || payload.length === 0) {
      console.warn('Gallery: No items to add to cart');
      return;
    }

    this.$store.galleryUI.setCartLoading(true);

    try {
      const cartResponse = await cartService.addItemsToCart(payload);

      if (cartResponse.sections && cartResponse.sections['ajax-cart']) {
        cartService.renderCartSection(cartResponse.sections['ajax-cart']);
      }

      await cartService.refreshCart();
      cartService.openMiniCart();

      this.$dispatch('gallery-items-added-to-cart', {
        items: payload,
        total: this.$store.galleryCore.computed_total_price,
        response: cartResponse
      });

      const itemsDescription = cartService.formatItemsForMessage(payload);
      this.$store.galleryUI.showSuccessMessage(`${itemsDescription} added to cart!`);

    } catch (error) {
      console.error('Failed to add gallery items to cart:', error);

      let alertMessage = 'Add to Cart Error\n\n';

      if (error.shopifyError) {
        alertMessage += `Status: ${error.status || 'Unknown'}\n`;
        alertMessage += `Message: ${error.shopifyError.message || 'Cart Error'}\n`;
        alertMessage += `Description: ${error.shopifyError.description || error.message}`;
      } else {
        alertMessage += error.message || 'Unable to add items to cart. Please try again.';
      }

      alert(alertMessage);

      const errorMessage = error.message?.includes('network')
        ? 'Network error. Please check your connection and try again.'
        : 'Unable to add items to cart. Please try again.';

      this.$store.galleryUI.setError(errorMessage);

      this.$dispatch('gallery-add-to-cart-error', {
        error: error.message,
        payload
      });
    } finally {
      this.$store.galleryUI.setCartLoading(false);
    }
  },

  preparePreviewImages() {
    const previewStore = this.$store?.galleryPreview;
    if (!previewStore?.preloadHighResImages) return;

    Promise.resolve(previewStore.preloadHighResImages('hover')).catch(error => {
      console.warn('Preview image preloading failed:', error);
    });
  },

  /**
   * Handles preview button click
   */
  async handlePreviewClick() {
    if (!this.$store) {
      console.error('Alpine stores not available');
      return;
    }

    if (!this.$store.galleryPreview) {
      console.error('Gallery preview store not initialized');
      return;
    }

    if (this.$store.galleryUI?.preview_button_disabled || this.$store.galleryPreview?.preview_transitioning) {
      return;
    }

    this.preparePreviewImages();

    try {
      if (!this.previewManager) {
        const { galleryPreviewManager } = await import('./gallery-preview-manager.js');
        this.previewManager = galleryPreviewManager;
        await this.previewManager.initialize();
      }

      await this.previewManager.enterPreview();
    } catch (error) {
      console.error('Error handling preview click:', error);
    }
  },

  /**
   * Handles exit preview button click
   */
  async handleExitPreviewClick() {
    if (!this.$store?.galleryPreview) {
      console.error('Gallery preview store not available');
      return;
    }

    const previewStore = this.$store.galleryPreview;

    if (previewStore.preview_transitioning || previewStore.zoom_transitioning) {
      return;
    }

    try {
      if (previewStore.zoom_active) {
        await previewStore.closeZoom();
      } else {
        if (this.previewManager) {
          await this.previewManager.exitPreview();
        } else {
          await previewStore.exitPreviewMode();
        }
      }
    } catch (error) {
      console.error('Error handling exit preview:', error);
    }
  },

  /**
   * Retries a failed operation by reloading products
   */
  retryOperation() {
    this.$store.galleryUI.resetRetry();
    this.loadCollectionProducts();
  },

  /**
   * Handles gallery exit with smart navigation
   */
  handleExit() {
    const currentUrl = window.location.pathname;
    const currentHost = window.location.host;

    const referrer = document.referrer;
    const isFromSameDomain = referrer && new URL(referrer).host === currentHost;

    if (window.history.length > 1 && isFromSameDomain) {
      window.history.back();

      setTimeout(() => {
        if (window.location.pathname === currentUrl) {
          window.location.href = ACTION_CONSTANTS.HOME_URL;
        }
      }, ACTION_CONSTANTS.NAVIGATION_FALLBACK_TIMEOUT_MS);
    } else {
      window.location.href = ACTION_CONSTANTS.HOME_URL;
    }
  }
};
